// src/components/ReaderAiDrawer.jsx
//
// Minimal, reliable AI drawer for a reader page. Hosts the existing AiStudyPartnerPanel
// (chat/history/error-bubble/disclaimer/mock-notice + its own sparkles header).
//
// Design: render NOTHING when closed (portal returns null) -> it cannot break layout or
// capture clicks. When open, a portal to document.body holds a dim backdrop + a fixed,
// width-capped panel with a normal-flow close bar. Three reliable close paths with zero
// branching: the X button, a backdrop click, and Esc.
//
// WHY A PORTAL: the reader shell keeps a lingering transform from `.page-fade-in`
// (animation fill-mode: forwards); a non-`none` transform on an ancestor makes
// `position: fixed` resolve against it instead of the viewport. Portaling to
// document.body avoids that. Portals preserve React context, so we re-apply the day/night
// skin from ReadingPrefsContext (night = `pack-themed`; day adds `reading-light`).
//
// Deliberately NOT here (kept simple): no slide animation, no off-screen positioning, no
// pointer-events juggling, no clip layer, no visualViewport keyboard math, no absolute X.
// Tradeoff: the chat thread resets each open (fresh mount) -- acceptable for v1.
//
// SECURITY NOTE: `entitled` is a UI hint only -- onAiChat re-derives entitlement and
// enforces the free-exchange cap server-side; the drawer cannot bypass it.
import React, { useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import AiStudyPartnerPanel from './AiStudyPartnerPanel.jsx';
import { ReadingPrefsContext } from '../pages/useReadingPrefs.js';
import { portalSkinClass } from '../pages/readingPrefs.js';

export default function ReaderAiDrawer({ open, onClose, packId, packTitle, entitled, currentItemId, currentItemType, currentItemTitle }) {
    const { colorMode } = useContext(ReadingPrefsContext);

    // Esc closes (listener only while open).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;

    const skin = portalSkinClass(colorMode);

    return createPortal(
        <div className={skin}>
            {/* Backdrop: one consistent translucent-dark scrim in BOTH skins so reader
                content stays visible behind the drawer. The colour is set inline (NOT via
                a `bg-black/30` class) on purpose: the day skin remaps
                `.reading-light.pack-themed .bg-black/30` to an OPAQUE cream, which would
                cover the content in day mode. A click outside always closes. */}
            <div className="fixed inset-0 z-[60]" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} onClick={onClose} aria-hidden="true" />

            {/* Panel: width-capped (max-w-[100vw] + box-border) so it can NEVER overflow
                the right edge / add horizontal scroll, at any viewport or after resize.
                top-16 = the 64px navbar (Navbar.jsx h-16). */}
            <div
                className="selectable fixed right-0 bottom-0 top-16 z-[61] flex flex-col w-full sm:w-[24rem] lg:w-[28rem] max-w-[100vw] box-border bg-book-pageDark border-l border-book-accent/30 shadow-[-10px_0_30px_rgba(0,0,0,0.45)]"
                role="dialog" aria-modal="true" aria-label="AI Study Partner">
                {/* The panel's own header now carries the close (X) -- a single compact
                    bar instead of a separate close strip + header, so the conversation
                    gets more height. Backdrop-click + Esc still close too. min-h-0 lets
                    the inner message list overflow/scroll. */}
                <div className="flex-1 min-h-0">
                    <AiStudyPartnerPanel packId={packId} packTitle={packTitle} entitled={entitled} onClose={onClose}
                        currentItemId={currentItemId} currentItemType={currentItemType} currentItemTitle={currentItemTitle} />
                </div>
            </div>
        </div>,
        document.body,
    );
}
